import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UsersThree, UserPlus, ShieldCheck } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { initials, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const empty = { name: "", email: "", password: "", platform_role: "manager" };

export default function AdminUsers() {
  const [list, setList] = useState(null);
  const [roles, setRoles] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/users").then((r) => { setList(r.data.items); setRoles(r.data.roles); }).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Platform user created");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const changeRole = async (u, platform_role) => {
    try { await api.post(`/admin/users/${u.id}/role`, { platform_role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleStatus = async (u) => {
    try { const { data } = await api.post(`/admin/users/${u.id}/status`); toast.success(`User ${data.status}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.name} (min 6 chars):`);
    if (!pw) return;
    try { await api.post(`/admin/users/${u.id}/password`, { password: pw }); toast.success("Password reset"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const [activity, setActivity] = useState(null);
  const openActivity = async (u) => {
    try { const { data } = await api.get(`/admin/users/${u.id}/activity`); setActivity({ user: u, logs: data }); }
    catch { toast.error("Failed to load activity"); }
  };

  const roleEntries = Object.entries(roles);

  return (
    <div className="space-y-5" data-testid="admin-users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-3xl flex items-center gap-2"><UsersThree size={26} className="text-primary" /> Users</h1>
          <p className="text-sm text-muted-foreground">Platform team accounts. All have admin access; the role is an organisational label.</p>
        </div>
        <Button data-testid="new-user-btn" onClick={() => setOpen(true)} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"><UserPlus size={17} className="mr-1.5" /> New user</Button>
      </div>

      <div className="card-surface divide-y divide-black/5" data-testid="users-list">
        {!list && <p className="p-6 text-center text-muted-foreground">Loading…</p>}
        {list && list.length === 0 && <p className="p-6 text-center text-muted-foreground">No platform users.</p>}
        {list && list.map((u) => (
          <div key={u.id} data-testid="user-row" className="p-4 flex items-center gap-4 flex-wrap">
            <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center font-display font-bold text-primary text-xs">{initials(u.name)}</div>
            <div className="flex-1 min-w-0">
              <p className="font-medium flex items-center gap-2 truncate">{u.name}{u.is_self && <span className="text-[10px] text-primary bg-primary/10 rounded-full px-2 py-0.5 shrink-0">You</span>}</p>
              <p className="text-xs text-muted-foreground truncate">{u.email} · joined {fmtDate(u.created_at)}</p>
            </div>
            <Select value={u.platform_role} onValueChange={(v) => changeRole(u, v)}>
              <SelectTrigger data-testid={`role-select-${u.id}`} className="w-40 h-9 bg-muted/40 border-black/10 rounded-xl text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{roleEntries.map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}</SelectContent>
            </Select>
            <span className={`text-xs px-3 py-1 rounded-full capitalize ${u.status === "active" ? "text-emerald-700 bg-emerald-600/10" : "text-destructive bg-destructive/10"}`}>{u.status}</span>
            <Button size="sm" variant="outline" data-testid={`activity-${u.id}`} onClick={() => openActivity(u)} className="rounded-full border-black/10">Activity</Button>
            <Button size="sm" variant="outline" data-testid={`reset-pw-${u.id}`} onClick={() => resetPassword(u)} className="rounded-full border-black/10">Reset password</Button>
            {!u.is_self && (
              <Button size="sm" variant="outline" data-testid={`toggle-status-${u.id}`} onClick={() => toggleStatus(u)} className={`rounded-full border-black/10 ${u.status === "active" ? "text-destructive" : "text-primary"}`}>
                {u.status === "active" ? "Suspend" : "Reactivate"}
              </Button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><ShieldCheck size={20} className="text-primary" /> New platform user</DialogTitle>
            <DialogDescription>They sign in on the Business / Admin tab with this email and password.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-muted-foreground">Full name</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" /></div>
            <div><Label className="text-xs text-muted-foreground">Email</Label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" /></div>
            <div><Label className="text-xs text-muted-foreground">Password</Label><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Role</Label>
              <Select value={form.platform_role} onValueChange={(v) => setForm({ ...form, platform_role: v })}>
                <SelectTrigger data-testid="user-role" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{roleEntries.map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button data-testid="create-user" onClick={create} disabled={saving || !form.email || !form.password} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">{saving ? "Creating…" : "Create user"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!activity} onOpenChange={(o) => !o && setActivity(null)}>
        <DialogContent data-testid="activity-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{activity?.user.name} · Activity</DialogTitle>
            <DialogDescription>Recent actions performed by this platform user.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {activity?.logs.length === 0 && <p className="text-sm text-muted-foreground">No recorded activity.</p>}
            {activity?.logs.map((l, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-black/5 pb-1.5">
                <span className="font-mono text-xs">{l.action} <span className="text-muted-foreground">· {l.entity_type}</span></span>
                <span className="text-[10px] text-muted-foreground">{fmtDate(l.created_at)}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
